import debounce from 'lodash/debounce';

class Steps {
	constructor(form, options = {}) {
		this.form = form;
		this.options = options;

		this.init();
	}

	init() {
		this.$steps = this.form.querySelectorAll('[data-form-steps="group"]');
		this.$submit = this.form.querySelector('[data-form-steps="submit"]');

		this._insertIndicators();
		this._insertNav();

		this.select();

		this.form.addEventListener(
			'bouncerShowError',
			debounce(
				event => {
					if (!event.target.closest.offsetParent) {
						return;
					}

					const step = event.target.closest('[data-form="group"]');
					const index = [].slice.call(this.$steps).indexOf(step);

					this.select(index);

					this._highlightIndicator(index);
				},
				100,
				{
					leading: true,
					trailing: false
				}
			)
		);

		this.form.addEventListener('bouncerRemoveError', event => {
			const step = event.target.closest('[data-form="group"]');
			const index = [].slice.call(this.$steps).indexOf(step);

			this._resetIndicator(index);
		});

		this.form.addEventListener('submit', () => {
			this.submissionAttempt = true;

			this.$submit.classList.add('is-active');
		});

		this.form.addEventListener(
			'focus',
			event => {
				const step = event.target.closest('[data-form="group"]');

				if (step) {
					const index = [].slice.call(this.$steps).indexOf(step);

					this.select(index);
				}
			},
			true
		);

		this.form.addEventListener(
			'LN:updateVisibility',
			debounce(() => {
				this.update();
			}, 100)
		);

		this.form.addEventListener('LN:showNextStep', () => {
			this.next();
		});
	}

	_insertIndicators() {
		const html = window.LN.templates
			.formStepsIndicators({
				steps: [].slice.call(this.$steps).map(item => item.dataset.formStepsTitle)
			})
			.trim();

		this.form.insertAdjacentHTML('afterbegin', html);

		this.$indicators = this.form.querySelectorAll('[data-form-steps-indicator]');
		this.$indicatorsWrapper = this.form.querySelector('[data-form-steps="indicators"]');

		this.$indicators.forEach(indicator => {
			indicator.addEventListener('click', event => {
				const index = parseInt(event.currentTarget.dataset.formStepsIndicator, 10);

				this.select(index);
			});
		});
	}

	_updateIndicators() {
		let visibleIndicators = 0;

		this.$indicators.forEach((indicator, i) => {
			if (this.$steps[i].offsetParent) {
				indicator.classList.add('is-active');

				visibleIndicators++;
			} else {
				indicator.classList.remove('is-active');
			}
		});

		const visibleWidth = `${(1 / visibleIndicators) * 100}%`;

		this.$indicators.forEach(indicator => {
			if (indicator.classList.contains('is-active')) {
				indicator.style.flexBasis = visibleWidth;
			} else {
				indicator.style.flexBasis = 0;
			}
		});
	}

	_highlightIndicator(step) {
		this.$indicators[step].classList.add('is-invalid');
	}

	_resetIndicator(step) {
		this.$indicators[step].classList.remove('is-invalid');
	}

	_resetIndicators() {
		this.$indicators.forEach((indicator, step) => {
			this._resetIndicator(step);
		});
	}

	_insertNav() {
		const html = window.LN.templates
			.formStepsNav({
				prev: window.LN.i18n.form__button__back || 'Previous',
				next: window.LN.i18n.form__button__next || 'Next'
			})
			.trim();

		this.$submit.insertAdjacentHTML('afterend', html);

		this.$nav = this.form.querySelector('[data-form-steps="nav"]');
		this.$prev = this.form.querySelector('[data-form-steps="prev"]');
		this.$next = this.form.querySelector('[data-form-steps="next"]');

		this.$prev.addEventListener('click', () => {
			this.prev();
		});
		this.$next.addEventListener('click', () => {
			this.next();
		});
	}

	_updateNav() {
		const steps = [].slice.call(this.$steps).map((node, i) => i);
		const visiblePrevSteps = steps
			.slice(0, this.activeStep)
			.filter(i => this.$steps[i].offsetParent);
		const visibleNextSteps = steps
			.slice(this.activeStep + 1)
			.filter(i => this.$steps[i].offsetParent);
		const [prevStep] = visiblePrevSteps.slice(-1);
		const [nextStep] = visibleNextSteps;
		const hideNavigation = this.$steps[this.activeStep].matches(
			'[data-form-steps-application]'
		);

		if (this.$steps.length - 1 === this.activeStep) {
			this.$nav.classList.remove('is-active');
		} else {
			this.$nav.classList.add('is-active');
		}

		if (prevStep !== undefined) {
			this.$prev.removeAttribute('disabled');
		} else {
			this.$prev.setAttribute('disabled', 'disabled');
		}

		if (nextStep !== undefined) {
			this.$next.removeAttribute('disabled');
		} else {
			this.$next.setAttribute('disabled', 'disabled');
		}

		this.prevStep = prevStep;
		this.nextStep = nextStep;

		if (hideNavigation) {
			this.$nav.setAttribute('hidden', 'hidden');
		} else {
			this.$nav.removeAttribute('hidden');
		}
	}

	update() {
		this._updateIndicators();
		this._updateNav();

		if (!this.$steps[this.activeStep].offsetParent) {
			this.select(this.nextStep, { skipValidation: true });
		}
	}

	async select(step = 0, { skipValidation } = {}) {
		if (this.activeStep === step || Number.isNaN(step)) {
			return;
		}

		if (
			this.activeStep !== undefined &&
			this.activeStep < step &&
			this.form.Bouncer &&
			!skipValidation
		) {
			const invalid = await this.form.Bouncer.validateAll(this.$steps[this.activeStep]);

			if (invalid.length) {
				let target = invalid[0];

				// Focus error message in case of hidden fields
				if (target.type === 'hidden') {
					const errorMessageId = target.getAttribute('aria-describedby');
					const errorMessage = document.querySelector(`#${errorMessageId}`);

					if (errorMessage) {
						errorMessage.setAttribute('tabindex', '-1');

						target = errorMessage;
					}
				}

				target.focus();

				return;
			}
		}

		this.activeStep = step;

		this.$indicators.forEach((indicator, i) => {
			if (i === this.activeStep) {
				indicator.classList.add('is-current');
			} else {
				indicator.classList.remove('is-current');
			}
		});

		this.$steps.forEach((step, i) => {
			if (i === this.activeStep) {
				step.classList.add('is-active');
			} else {
				step.classList.remove('is-active');
			}
		});

		if (!this.submissionAttempt) {
			let lastActiveStep = 0;

			this.$steps.forEach((step, i) => {
				if (step.offsetParent) {
					lastActiveStep = i;
				}
			});

			if (lastActiveStep && lastActiveStep === this.activeStep) {
				this.$submit.classList.add('is-active');
			} else {
				this.$submit.classList.remove('is-active');
			}
		}

		this.update();

		this._updateScrollPosition();

		if (this.options.onShow) {
			this.options.onShow(this.form);
		}

		setTimeout(() => {
			this.form.dispatchEvent(new CustomEvent('LN:updateStep'));
		});
	}

	prev() {
		if (this.prevStep !== undefined) {
			this.select(this.prevStep);
		}
	}

	next() {
		if (this.nextStep !== undefined) {
			this.select(this.nextStep);
		}
	}

	_updateScrollPosition() {
		const maxPosition = this.$indicatorsWrapper.offsetTop;
		const currentScrollPosition = document.documentElement.scrollTop || document.body.scrollTop;

		if (currentScrollPosition > maxPosition) {
			this.$indicatorsWrapper.scrollIntoView({ behavior: 'smooth' });
		}
	}
}

function initSteps(form, options) {
	new Steps(form, options);
}

export { Steps, initSteps };
